import type ro from "./ro";
const en: typeof ro = {
  configuredOffer: {
    title: "Your configured tour offer",
    subtitle:
      "Present the chosen rooms, boarding point and services in a personalised client proposal.",
    create: "Offer with my selections",
    createHint: "You can prepare an offer before entering traveller details.",
    back: "Back to configuration",
    showSuggestions: "Show available optional extras",
    switchHint:
      "Suggestions appear separately and do not change the offer total.",
    configuredTotal: "Your configured tour",
    totalHint:
      "Total for all travellers, including taxes and selected services. Optional suggestions are excluded.",
    priceBasis:
      "This offer reflects the rooms, boarding point and services selected for the travellers above.",
    yourConfiguration: "Configured for you",
    breakdown: "Your fare breakdown",
    selectedServices: "Services in your configuration",
    suggestionsTitle: "More to explore",
    makeItYours: "Optional · your choice",
    suggestionsHint:
      "You can add the services below. Prices are additional costs for the indicated travellers and are excluded from the total above.",
    notIncluded: "Optional · excluded from total",
    includedTotal: "Included in the configured total",
    person: "Room {room} · traveller {person}",
    priceOnRequest: "Price on request",
    estimate:
      "Individually calculated service fares are estimates and will be reconfirmed when booking.",
    unready:
      "Check the room and service selections and wait for fare checks to finish. Select at least one traveller for an individual service.",
    standardServices:
      "General tour information appears below. Services added to this offer are detailed in the configuration above.",
    seats: "Preferred coach seats",
    seatsHint:
      "Selected seats are preferences and will be confirmed when booking.",
  },
  tourOffers: {
    title: "Tour offers",
    defaultTitle: "Your next journey starts here",
    subtitle:
      "Turn selected departures into a personalised proposal, ready to share with your client.",
    back: "Back to tours",
    selectionHint:
      "Select a whole tour or individual departure dates from your search results.",
    selectTour: "Select tour for offer",
    selectDate: "Select for offer",
    eligibleDates: "{count} priced departures, including those on request",
    selected: "{count} departures selected for offer",
    create: "Create offer",
    room: "Room {number}",
    adults: "{count} adults",
    children: "children — ages: {ages} years",
    priceBasis:
      "Total fares for all rooms and travellers above, including taxes. Each departure is an alternative. Additional options selected during booking may change the price.",
    tooLarge:
      "This offer is too large to generate. Reduce the number of departures or shorten the text.",
  },

  app: {
    partners: "Partner portal",
    tagline: "Beautiful holidays. Trusted partnerships.",
    hello: "Hello, partner!",
    intro: "The next great holiday starts here.",
    workspace: "Your workspace",
    copyright: "Hello Holidays · Travel agency partner portal",
    support: "By your side, with every booking",
  },
  nav: {
    home: "Home",
    packages: "Holiday packages",
    hotels: "Hotels",
    villas: "Villas",
    tours: "Tours",
    bus: "Coach tickets",
    bookings: "Bookings",
    account: "My account",
    contact: "Contact",
    logout: "Sign out",
    offers: "My offers",
  },
  common: {
    search: "Search offers",
    loading: "Loading…",
    retry: "Try again",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    remove: "Remove",
    select: "Select",
    all: "All",
    details: "View details",
    error: "We could not complete your request. Please try again.",
    empty: "No results found",
    emptyHint: "Try a different destination or travel dates.",
    reset: "Reset",
    back: "Back",
    continue: "Continue",
    required: "Complete all required fields.",
    unavailable: "Unavailable",
    open: "Open",
    language: "Language",
    menu: "Menu",
    selectOffer: "Select offer",
  },
  auth: {
    welcome: "Welcome back!",
    subtitle: "Sign in and turn your clients’ plans into holidays.",
    client: "Agency code",
    username: "Username",
    password: "Password",
    login: "Sign in",
    expired: "Your session has expired. Sign in to continue.",
    failed: "Sign-in failed. Check your details and try again.",
    partner: "Your partner for memorable holidays.",
    partnerText:
      "Favourite destinations, carefully chosen offers and everything you need for happy clients.",
    help: "Need help accessing your account?",
    secure: "Dedicated access for partner agencies",
    restoreFailed:
      "Unable to check your session. Check your connection and retry.",
  },
  search: {
    roomCount: "no rooms | one room | {count} rooms",
    travellerCount: "no travellers | one traveller | {count} travellers",
    title: "Find the perfect holiday",
    subtitle: "Less time searching. More inspiration for your clients.",
    destination: "Destination",
    country: "Country",
    resort: "Resort",
    area: "Area",
    departure: "Departing from",
    transport: "Transport",
    flight: "Flight",
    bus: "Coach",
    own: "Own transport",
    date: "Departure date",
    checkIn: "Check-in",
    checkOut: "Check-out",
    duration: "Duration",
    nights: "nights",
    rooms: "Rooms and travellers",
    room: "Room",
    adults: "Adults",
    children: "Children",
    age: "Child age",
    addRoom: "Add room",
    hotels: "Preferred hotels",
    allHotels: "All hotels in this destination",
    available: "Available only",
    currency: "Currency",
    product: "Holiday type",
    ready: "Where shall we send them?",
    readyHint:
      "Choose a destination, travel dates and travellers. We’ll find the options.",
    results: "{count} hotels found",
    images: "Show images",
    sort: "Sort by",
    price: "Price: low to high",
    priceDesc: "Price: high to low",
    hotel: "Hotel name",
    stars: "Star rating",
    selected: "{count} offers selected",
    selectionHint: "Compare and prepare a personalised proposal.",
    searching: "Finding the best options…",
    datesHint: "The calendar shows available departure dates only.",
    invalidDates: "Select valid travel dates.",
    meal: "Board basis",
    total: "Total price",
    from: "from",
    onRequest: "On request",
    confirmed: "Available",
    book: "Book",
    taxes: "Taxes",
    commission: "Commission",
    discount: "Discount",
    paymentSchedule: "Payment schedule",
    warnings: "Conditions",
    noDates: "No departures are available for this selection.",
  },
  offers: {
    stays: "Packages and hotels",
    title: "A proposal made for your client",
    subtitle: "Your selected offers, clearly presented and personalised.",
    client: "Client name",
    notes: "Message for your client",
    markup: "Markup (%)",
    images: "Include photographs",
    preview: "Preview",
    pdf: "Download PDF",
    print: "Print",
    clear: "Clear selection",
    empty: "You haven’t selected any offers yet.",
    disclaimer:
      "Prices and availability are reconfirmed at the time of booking.",
    titleLabel: "Offer title",
    defaultTitle: "Your holiday proposal",
    generated: "Offer prepared by",
    invalid: "This offer is unavailable. Return to search and select again.",
    pdfSetup:
      "PDF export requires eTrip to be configured for this application.",
  },
  booking: {
    editDocuments: "Edit contact / document details",
    gender: "Sex",
    documentNo: "Document number",
    title: "Complete your booking",
    tourists: "Traveller details",
    firstName: "First name",
    lastName: "Last name",
    birthday: "Date of birth",
    titleLabel: "Title",
    notes: "Notes",
    terms:
      "I have checked the traveller details and accept the booking conditions.",
    confirm: "Confirm booking",
    option: "Optional booking",
    success: "Booking registered",
    reference: "Booking reference",
    pending: "Your booking is being processed. Please wait.",
    uncertain:
      "We did not receive confirmation. Check your bookings before submitting again.",
    number: "Number",
    passenger: "Lead traveller",
    date: "Booking date",
    travelDate: "Travel date",
    status: "Status",
    paid: "Paid",
    remaining: "Balance",
    keyword: "Keyword",
    documents: "Documents",
    voucher: "Voucher",
    contract: "Contract",
    invoice: "Invoice",
    view: "Booking details",
    email: "Email",
    phone: "Phone",
    noPermission: "Your account does not allow online bookings.",
  },
  home: {
    eyebrow: "TOGETHER, WE CREATE HOLIDAYS",
    packagesText: "Holidays by plane, coach or your own transport.",
    hotelsText: "The right hotel for every kind of holiday.",
    toursText: "Itineraries that turn journeys into stories.",
    bookingsText: "All your bookings in one place.",
    quick: "Where do we start today?",
    inspiration: "From the first search to departure.",
    inspirationText:
      "Compare options, select the right offers and prepare a proposal that inspires.",
    step1: "Discover",
    step1Text: "Find the right destination and travel dates.",
    step2: "Personalise",
    step2Text: "Choose exactly the offers you want to present.",
    step3: "Book",
    step3Text: "Check the details and confirm the holiday.",
  },
  account: {
    agency: "Your agency",
    profile: "User profile",
    company: "Company name",
    vat: "VAT number",
    address: "Address",
    bank: "Bank",
    iban: "Bank account",
  },
  bus: {
    direction: "Journey type",
    return: "Return",
    out: "Outbound only",
    in: "Inbound only",
    returnDate: "Return date",
    dropOff: "Drop-off point / hotel",
  },
  tourBooking: {
    back: "Back to tours",
    eyebrow: "Tour booking",
    title: "Prepare the journey",
    travellers: "{count} travellers",
    summary: "Booking summary",
    roomsTravellers: "Rooms and travellers",
    roomsHint:
      "Compare fares and choose a room for each group of travellers. Differences are reflected in the total immediately.",
    departureTitle: "Departure point",
    departureHint:
      "Choose the boarding location. Each choice shows the transfer fare and its difference from the base price.",
    servicesTitle: "Services and experiences",
    servicesHint:
      "Review included services and add the right options for each traveller.",
    components: "Tour components",
    supplements: "Supplements and optional services",
    contactTitle: "Contact and notes",
    contactHint:
      "Enter the traveller’s phone number, reachable at departure with roaming enabled. These details are used for operational updates and emergencies.",
    conditions: "Booking conditions",
    cancellation: "Cancellation and penalties",
    cancellationText:
      "Cancellations follow the deadlines in the annex to the travel services contract. Penalties apply according to the deadlines recorded in the booking.",
    acceptTransfers: "I have read and accept the",
    transferConditions: "General conditions for domestic transfers",
    noUpgrade: "No supplement",
    totalChange: "change to the booking total",
    roomFare: "Room fare, including taxes",
    departureFare: "Departure fare, including taxes",
    roomsLeft: "{count} rooms remaining",
    includedPrice: "Included in the fare",
    baseFare: "Base fare",
    roomUpgrades: "Room differences",
    departureUpgrade: "Departure point difference",
    optionalExtras: "Optional services",
    totalHint: "Including taxes, for all selected rooms and travellers.",
    balanceDue: "Balance due",
    updatingSchedule: "Updating the payment schedule…",
    bookOption: "Book as an option",
    optionHint:
      "Option bookings are available for departures at least 30 days from today.",
    passengerHint:
      "Enter names as shown on travel documents. Travellers under 18 are children; ages are checked on the departure date.",
    adult: "Adult",
    childAge: "Child · age {age}",
    ageMismatch:
      "The date of birth must match the searched age on the departure date.",
    extraAllTravellers:
      "Fare for all travellers. Select below who will receive this service.",
    extraTotal: "Total fare for the additional service.",
    noRooms: "No room options exist for this group. Please search again.",
    chooseRooms:
      "Select an available room for every group and check the departure point and services.",
    invalid:
      "Complete traveller details and phone number, check ages and accept both sets of conditions.",
    expired:
      "The search has expired or fares, availability or travellers have changed. Search again before booking.",
    checkFailed:
      "We could not verify the latest availability. No booking was submitted. Please try again.",
    viewBooking: "View booking",
    outbound: "Outbound",
    inbound: "Inbound",
    invoiceClient: "Individual invoicing client",
    address: "Address",
    city: "City",
    perPaxEstimate:
      "Fares for services selected per traveller are estimates; related discounts and commission are confirmed in the booking.",
  },
  tours: {
    amountPending: "Amount to be confirmed",
    findTour: "Find the right tour",
    destinations: "Destinations",
    allDestinations: "All countries and destinations",
    selectedDestinations: "{count} destinations selected",
    areas: "Areas",
    categories: "Categories",
    departures: "Departure points",
    selectedFilters: "{0} selected",
    matchAll: "Match all selected categories",
    allMatchHint: "The tour must belong to every selected category.",
    anyMatchHint: "The tour can belong to any selected category.",
    periodDuration: "Dates and duration",
    departureRangeHint:
      "These dates bound the tour departure date. Either end of the range can be left blank.",
    departureFrom: "Earliest departure",
    departureTo: "Latest departure",
    durationDays: "Duration (days)",
    accommodationNights: "Accommodation (nights)",
    minDuration: "Minimum duration in days",
    maxDuration: "Maximum duration in days",
    minAccommodation: "Minimum number of nights",
    maxAccommodation: "Maximum number of nights",
    reset: "Reset filters",
    invalidFilters:
      "Check date, day and night ranges and traveller ages. Minimum values must not exceed maximum values.",
    optionsError: "Some filters could not be loaded. Retry to see all options.",
    results: "No tours found | One tour found | {count} tours found",
    resultsHint:
      "Compare departures and fares for the travellers in your search.",
    ready: "What will you discover next?",
    readyHint:
      "Choose destinations or explore every tour. Refine by theme, travel dates and departure point.",
    days: "{count} days",
    nights: "{count} nights",
    partyTotal: "total for searched travellers",
    departureDates: "Departure dates",
    allDates: "All {count} departures",
    fewerDates: "Fewer departures",
    unavailable: "Unavailable",
    unknownAvailability: "Availability unconfirmed",
    noPrice: "Price unconfirmed",
    priceDetails: "Price details",
    priceBreakdown: "Fare breakdown",
    includedDiscount: "Discount already applied",
    netTotal: "Agency amount payable",
    priceHint:
      "The total includes taxes and applied discounts for the rooms and travellers searched. Booking choices may change the fare.",
    appliedDiscounts: "Applied discounts",
    bookingValidity: "Booking period",
    travelDates: "Travel dates",
    noSchedule: "No payment schedule is available for this departure.",
    cancellationHint:
      "Consult the operator terms and cancellation deadlines in the booking documents.",
    operatorTerms: "Hello Holidays terms",
    previousPhoto: "Previous photograph",
    nextPhoto: "Next photograph",
    sort: {
      price: "Price: low to high",
      priceDesc: "Price: high to low",
      name: "Tour name",
      departure: "First departure",
    },
    seat: "Seat",
    seats: "Choose coach seats",
    title: "Discover Hello tours",
    keyword: "Search for a tour",
    from: "From",
    to: "Until",
    itinerary: "Itinerary",
    included: "Included services",
    excluded: "Excluded services",
    dates: "Departures",
    selectDate: "Select departure",
    roomType: "Room type",
    departurePoint: "Boarding point",
  },
};
export default en;
